/**
 * Import OpenPolotno store JSON (or ZIP containing design JSON + optional assets).
 */
import { unzipSync } from 'fflate';
import type { RaeditorStore } from '@reyka/openpolotno';
import { ensureStoreFonts } from './ensureStoreFonts';

export type ImportResult =
  | { ok: true; message: string }
  | { ok: false; message: string };

type StoreJson = {
  pages?: unknown[];
  [key: string]: unknown;
};

function isStoreJson(value: unknown): value is StoreJson {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    Array.isArray((value as StoreJson).pages) &&
    (value as StoreJson).pages!.length >= 0
  );
}

function parseJsonText(text: string): unknown {
  return JSON.parse(text);
}

function basename(path: string): string {
  const parts = path.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || path;
}

function dirname(path: string): string {
  const norm = path.replace(/\\/g, '/');
  const i = norm.lastIndexOf('/');
  return i >= 0 ? norm.slice(0, i) : '';
}

function joinPath(baseDir: string, rel: string): string {
  const cleaned = rel.replace(/^\.\//, '');
  if (!baseDir) return cleaned;
  return `${baseDir.replace(/\/+$/, '')}/${cleaned}`;
}

function mimeFromPath(path: string): string {
  const lower = path.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  if (lower.endsWith('.json')) return 'application/json';
  return 'application/octet-stream';
}

/** Prefer design.json / template.json / store.json, else only pages JSON, else largest root-level. */
function pickPrimaryJson(
  entries: Map<string, Uint8Array>,
): { path: string; json: StoreJson } | null {
  const preferredNames = new Set(['design.json', 'template.json', 'store.json']);
  const jsonPaths = [...entries.keys()].filter((p) => /\.json$/i.test(p) && !p.endsWith('/'));

  const candidates: Array<{ path: string; json: StoreJson; size: number; root: boolean; preferred: boolean }> =
    [];

  for (const path of jsonPaths) {
    const bytes = entries.get(path);
    if (!bytes) continue;
    try {
      const text = new TextDecoder().decode(bytes);
      const parsed = parseJsonText(text);
      if (!isStoreJson(parsed)) continue;
      const name = basename(path).toLowerCase();
      const depth = path.replace(/\\/g, '/').split('/').filter(Boolean).length;
      candidates.push({
        path,
        json: parsed,
        size: bytes.byteLength,
        root: depth === 1,
        preferred: preferredNames.has(name),
      });
    } catch {
      /* skip invalid json */
    }
  }

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => {
    if (a.preferred !== b.preferred) return a.preferred ? -1 : 1;
    if (a.root !== b.root) return a.root ? -1 : 1;
    return b.size - a.size;
  });

  const best = candidates[0]!;
  return { path: best.path, json: best.json };
}

function rewriteRelativeAssetSrcs(
  json: StoreJson,
  entries: Map<string, Uint8Array>,
  jsonPath: string,
): StoreJson {
  const jsonDir = dirname(jsonPath);
  const blobUrls = new Map<string, string>();

  const resolveEntry = (rel: string): Uint8Array | undefined => {
    const candidates = [
      joinPath(jsonDir, rel),
      rel.replace(/^\.\//, ''),
      joinPath(jsonDir, rel.replace(/^\.\//, '')),
      // strip leading assets/ variants
      joinPath(jsonDir, rel.replace(/^\.?\/*assets\//, 'assets/')),
    ];
    // Also try matching by basename anywhere in the zip
    const base = basename(rel);
    for (const key of entries.keys()) {
      if (basename(key) === base) candidates.push(key);
    }
    for (const c of candidates) {
      const norm = c.replace(/\\/g, '/');
      if (entries.has(norm)) return entries.get(norm);
      // case-insensitive fallback
      for (const [k, v] of entries) {
        if (k.replace(/\\/g, '/').toLowerCase() === norm.toLowerCase()) return v;
      }
    }
    return undefined;
  };

  const toBlobUrl = (rel: string): string | null => {
    if (blobUrls.has(rel)) return blobUrls.get(rel)!;
    const bytes = resolveEntry(rel);
    if (!bytes) return null;
    const mime = mimeFromPath(rel);
    const blob = new Blob([bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer], { type: mime });
    const url = URL.createObjectURL(blob);
    blobUrls.set(rel, url);
    return url;
  };

  const rewriteValue = (value: unknown): unknown => {
    if (typeof value === 'string') {
      // Relative asset refs only (skip http(s), data:, blob:)
      if (
        /^(https?:|data:|blob:|\/\/)/i.test(value) ||
        value.startsWith('/') ||
        !/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(value)
      ) {
        return value;
      }
      if (value.startsWith('./') || value.startsWith('../') || value.startsWith('assets/') || !value.includes('://')) {
        const url = toBlobUrl(value);
        return url ?? value;
      }
      return value;
    }
    if (Array.isArray(value)) return value.map(rewriteValue);
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = rewriteValue(v);
      }
      return out;
    }
    return value;
  };

  return rewriteValue(json) as StoreJson;
}

async function loadStoreJson(store: RaeditorStore, json: StoreJson): Promise<void> {
  store.loadJSON(json);
  await ensureStoreFonts(store);
}

export async function importDesignFromFile(
  store: RaeditorStore,
  file: File,
): Promise<ImportResult> {
  const name = (file.name || '').toLowerCase();
  try {
    if (name.endsWith('.json') || file.type === 'application/json' || file.type === 'text/json') {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = parseJsonText(text);
      } catch {
        return { ok: false, message: 'Import failed — invalid JSON' };
      }
      if (!isStoreJson(parsed)) {
        return { ok: false, message: 'Import failed — JSON must have a pages array' };
      }
      await loadStoreJson(store, parsed);
      return { ok: true, message: 'Imported design from file' };
    }

    if (name.endsWith('.zip') || file.type === 'application/zip' || file.type === 'application/x-zip-compressed') {
      const buf = new Uint8Array(await file.arrayBuffer());
      let unzipped: Record<string, Uint8Array>;
      try {
        unzipped = unzipSync(buf);
      } catch {
        return { ok: false, message: 'Import failed — could not read ZIP' };
      }
      const entries = new Map<string, Uint8Array>();
      for (const [path, data] of Object.entries(unzipped)) {
        if (path.endsWith('/')) continue;
        // Strip single top-level folder if present (e.g. design/design.json)
        entries.set(path.replace(/\\/g, '/'), data);
      }

      const picked = pickPrimaryJson(entries);
      if (!picked) {
        return { ok: false, message: 'Import failed — no valid design JSON with pages in ZIP' };
      }
      const rewritten = rewriteRelativeAssetSrcs(picked.json, entries, picked.path);
      await loadStoreJson(store, rewritten);
      return { ok: true, message: 'Imported design from file' };
    }

    return { ok: false, message: 'Import failed — choose a .json or .zip file' };
  } catch (err) {
    console.error(err);
    return { ok: false, message: 'Import failed — unexpected error' };
  }
}

export function downloadStoreJson(store: RaeditorStore, fileName = 'design.json'): void {
  const json = store.toJSON();
  const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
